import { useCallback, useEffect, useRef, useState } from "react";

interface HoverIntent {
  /** True once the pointer has *stayed* for `delayMs`; back to false once it
   *  has *left* for `delayMs`. */
  active: boolean;
  /** Force the intent back to idle (clear `active` + any pending dwell timer).
   *  Use when an interaction ends the casual-hover gesture (e.g. a right-click)
   *  so a later layout change can't fire off the stale dwell. */
  reset: () => void;
  handlers: {
    onPointerEnter: () => void;
    onPointerLeave: () => void;
  };
}

/**
 * Deliberate hover: only flips `active` after the pointer dwells (or leaves) for
 * `delayMs`. Used for the calendar's >1s hover-to-expand / >1s revert, but it's
 * generic — any "expand on sustained hover" surface can reuse it.
 */
export function useHoverIntent(delayMs = 1000): HoverIntent {
  const [active, setActive] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);

  const onPointerEnter = useCallback(() => {
    clear();
    timer.current = setTimeout(() => setActive(true), delayMs);
  }, [clear, delayMs]);

  const onPointerLeave = useCallback(() => {
    clear();
    timer.current = setTimeout(() => setActive(false), delayMs);
  }, [clear, delayMs]);

  const reset = useCallback(() => {
    clear();
    setActive(false);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return { active, reset, handlers: { onPointerEnter, onPointerLeave } };
}
